import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../../config/api";
import {
  History,
  Search,
  Loader2,
  RefreshCw,
  Container,
} from "lucide-react";


const STATUS_BADGE_CLASSES = {
  "in transit": "bg-blue-100 text-blue-700",
  "arrived": "bg-green-100 text-green-700",
  "discharged": "bg-emerald-100 text-emerald-700",
  "loaded": "bg-cyan-100 text-cyan-700",
  "departed": "bg-indigo-100 text-indigo-700",
  "delivered": "bg-green-100 text-green-700",
  "pending": "bg-yellow-100 text-yellow-700",
};

const getStatusBadgeClass = (status) => {
  if (!status) return "bg-gray-100 text-gray-600";
  const lowerStatus = status.toLowerCase();
  for (const [key, value] of Object.entries(STATUS_BADGE_CLASSES)) {
    if (lowerStatus.includes(key)) return value;
  }
  return "bg-gray-100 text-gray-600";
};

export default function TrackingHistoryContent({ onTrackAgain }) {
  const { getAuthHeaders } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tracking/history`, {
        headers: getAuthHeaders(),
      });
      setHistory(response.data || []);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Failed to fetch tracking history");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1800px] mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Tracking History</h2>
            <p className="text-sm text-muted-foreground">
              View your past container tracking queries
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchHistory}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* History Table */}
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <History className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No tracking history yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Your tracking queries will appear here
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Container Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Last Tracked</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-mono font-medium">
                      <div className="flex items-center gap-2">
                        <Container className="h-4 w-4 text-slate-400" />
                        {item.container_number}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(item.status)}`}>
                        {item.status || "Unknown"}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600">{item.location || "-"}</TableCell>
                    <TableCell className="text-slate-600">{item.port_of_discharge || "-"}</TableCell>
                    <TableCell className="text-slate-500">
                      {item.tracked_at
                        ? new Date(item.tracked_at).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onTrackAgain?.(item.container_number)}
                      >
                        <Search className="h-4 w-4 mr-1" />
                        Track Again
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </motion.div>
    </div>
  );
}
